import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Check, Loader2, Sparkles, Layers, Globe2, Cpu, Database, Radio, BarChart3 } from "lucide-react";
import { XR, xrSession } from "../lib/xr";

const INTEREST_OPTIONS = [
  { v: "investor", l: "Investor" },
  { v: "school", l: "School" },
  { v: "university", l: "University" },
  { v: "museum", l: "Museum" },
  { v: "cultural", l: "Cultural institution" },
  { v: "technology", l: "Technology partner" },
  { v: "content", l: "Content partner" },
  { v: "other", l: "Other" },
];

export default function XRPartners() {
  return (
    <div className="mythos-ink-bg min-h-screen" data-testid="xr-partners-page">
      <section className="max-w-6xl mx-auto px-5 md:px-10 pt-20 pb-16">
        <div className="text-[11px] tracking-[0.28em] uppercase mythos-gold font-bold mb-6">EduSphere XR · For partners &amp; investors</div>
        <h1 className="font-mythos text-5xl md:text-7xl font-extrabold tracking-tight leading-[1.02] text-[#F4EFE6] max-w-4xl">
          THE <span className="mythos-gold">BIGGER</span> VISION.
        </h1>
        <p className="text-[#E8EBF0]/70 mt-6 max-w-2xl text-lg leading-relaxed">
          EduSphere is building an experiential learning platform where India's history, culture, science and knowledge can be explored through interactive worlds.
        </p>
        <div className="mt-6 flex flex-wrap gap-3">
          <Link to="/xr" className="border border-[#2A3349] hover:border-[#C9A66B] text-[#F4EFE6]/80 hover:text-[#F4EFE6] rounded-full px-5 py-2.5 text-sm transition" data-testid="xr-partners-back">
            ← Back to the experience
          </Link>
        </div>
      </section>

      {/* ONE PLATFORM */}
      <section className="max-w-6xl mx-auto px-5 md:px-10 py-16">
        <div className="text-[11px] tracking-[0.24em] uppercase mythos-gold font-bold mb-3">One platform</div>
        <h2 className="font-mythos text-3xl md:text-4xl font-extrabold tracking-tight text-[#F4EFE6]">A single pipeline. Many worlds.</h2>
        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {[
            { t: "Stories", d: "Narrative tradition rendered as interactive, culturally respectful experience." },
            { t: "3D Worlds", d: "Reusable world engine. Add a new civilisation in weeks, not quarters." },
            { t: "Interactive simulations", d: "Decisions have consequences. Learning sticks because it was experienced." },
            { t: "AI Guides", d: "In-character counsel grounded in curated knowledge — never a parrot of the open web." },
            { t: "Learning", d: "Short, embedded moments of reflection — not quizzes bolted onto games." },
            { t: "Assessment", d: "Concepts explored, not scores earned. Discovery is the metric." },
          ].map((s, i) => (
            <div key={i} className="vault-card rounded-2xl p-5 border border-[#2A3349]">
              <div className="font-mythos font-bold text-[#F4EFE6]">{s.t}</div>
              <div className="text-xs text-[#E8EBF0]/65 mt-2">{s.d}</div>
            </div>
          ))}
        </div>
      </section>

      {/* SCALABILITY — Today → Platform → Ecosystem */}
      <section className="max-w-6xl mx-auto px-5 md:px-10 py-16">
        <div className="text-[11px] tracking-[0.24em] uppercase mythos-gold font-bold mb-3">Scalability</div>
        <h2 className="font-mythos text-3xl md:text-4xl font-extrabold tracking-tight text-[#F4EFE6]">From a prototype to an ecosystem.</h2>
        <div className="mt-10 grid md:grid-cols-4 gap-4">
          {[
            { k: "TODAY", t: "One prototype world", d: "Kurukshetra. Proof of concept — vertical slice." },
            { k: "NEXT", t: "10 immersive worlds", d: "Nalanda · Hampi · Chola · Indus Valley · Astronomy · Forts · Vijayanagara · Pataliputra · Ajanta · Freedom movement." },
            { k: "PLATFORM", t: "Reusable World Engine", d: "A single toolchain for environments, characters, missions, knowledge, and assessment." },
            { k: "ECOSYSTEM", t: "Many partners", d: "Schools · Museums · Universities · Cultural institutions · Families · Learners worldwide." },
          ].map((p, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.08 }}
              className="vault-card rounded-2xl p-5 border border-[#2A3349] relative"
            >
              <div className="text-[10px] tracking-[0.22em] uppercase mythos-gold font-bold">{p.k}</div>
              <div className="font-mythos font-extrabold text-[#F4EFE6] mt-2 text-lg">{p.t}</div>
              <div className="text-xs text-[#E8EBF0]/65 mt-2 leading-relaxed">{p.d}</div>
              {i < 3 && (
                <ArrowRight className="hidden md:block absolute -right-5 top-1/2 -translate-y-1/2 mythos-gold" size={18} />
              )}
            </motion.div>
          ))}
        </div>
        <div className="mt-10 text-sm text-[#C9A66B] font-bold italic">
          Kurukshetra is the proof of concept. The platform is the opportunity.
        </div>
      </section>

      {/* BUILT FOR */}
      <section className="max-w-6xl mx-auto px-5 md:px-10 py-16">
        <div className="text-[11px] tracking-[0.24em] uppercase mythos-gold font-bold mb-3">Built for</div>
        <div className="flex flex-wrap gap-2">
          {[
            { i: Globe2, t: "Web", s: "shipped" },
            { i: Layers, t: "3D", s: "shipped" },
            { i: Radio, t: "WebXR", s: "roadmap" },
            { i: Cpu, t: "AI", s: "shipped" },
            { i: Database, t: "RAG", s: "roadmap" },
            { i: Sparkles, t: "Cloud", s: "shipped" },
            { i: Globe2, t: "Multiplayer", s: "roadmap" },
            { i: BarChart3, t: "Analytics", s: "shipped" },
          ].map((x, i) => (
            <div key={i} className="inline-flex items-center gap-2 vault-card rounded-full px-4 py-2 border border-[#2A3349]">
              <x.i size={14} className={x.s === "shipped" ? "mythos-gold" : "text-[#7d8294]"} />
              <span className="text-sm text-[#F4EFE6] font-semibold">{x.t}</span>
              <span className={`text-[9px] tracking-[0.18em] uppercase font-bold ${x.s === "shipped" ? "mythos-gold" : "text-[#7d8294]"}`}>· {x.s}</span>
            </div>
          ))}
        </div>
        <p className="text-xs text-[#E8EBF0]/55 mt-5 italic">We label every capability as shipped or roadmap. No feature is claimed before it exists.</p>
      </section>

      {/* INTEREST FORM */}
      <PartnerInterestForm />

      <div className="max-w-6xl mx-auto px-5 md:px-10 pb-20 text-xs text-[#F4EFE6]/40">
        A Mythos India Studios Private Limited product. CIN: U62010TS2026PTC221089. Visit <a href="https://mythosindiastudio.com/" target="_blank" rel="noopener noreferrer" className="hover:text-[#C9A66B]">mythosindiastudio.com</a>.
      </div>
    </div>
  );
}

function PartnerInterestForm() {
  const [form, setForm] = useState({ name: "", organisation: "", email: "", interest: "investor", message: "" });
  const [status, setStatus] = useState("idle"); // idle | loading | success | error
  const [err, setErr] = useState("");

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      setStatus("error"); setErr("Name and email are required."); return;
    }
    setStatus("loading"); setErr("");
    try {
      await XR.partnerInterest(form);
      await XR.event("partner_interest_clicked", { interest: form.interest }, xrSession());
      setStatus("success");
    } catch (ex) {
      setStatus("error");
      setErr(ex?.response?.data?.detail || "Could not submit. Try again shortly.");
    }
  };

  if (status === "success") {
    return (
      <section className="max-w-3xl mx-auto px-5 md:px-10 py-16" data-testid="xr-partner-success">
        <div className="vault-card rounded-2xl p-10 border border-[#C9A66B] text-center">
          <div className="w-16 h-16 rounded-full mythos-gold-bg grid place-items-center mx-auto mb-5">
            <Check size={28} className="text-[#0E0F12]" />
          </div>
          <h3 className="font-mythos text-2xl font-extrabold text-[#F4EFE6]">Thank you.</h3>
          <p className="text-[#E8EBF0]/75 mt-3 max-w-md mx-auto">Your interest has been recorded. We review each submission personally and will be in touch.</p>
          <Link to="/xr" className="mt-7 inline-flex items-center gap-2 border border-[#C9A66B] text-[#C9A66B] rounded-full px-5 py-2.5 text-sm font-bold hover:bg-[#C9A66B] hover:text-[#0E0F12] transition">
            Back to the experience <ArrowRight size={14} />
          </Link>
        </div>
      </section>
    );
  }

  return (
    <section className="max-w-3xl mx-auto px-5 md:px-10 py-16" data-testid="xr-partner-form-section">
      <div className="text-[11px] tracking-[0.24em] uppercase mythos-gold font-bold mb-3">Partner with us</div>
      <h2 className="font-mythos text-3xl md:text-4xl font-extrabold tracking-tight text-[#F4EFE6]">EXPLORE PARTNERSHIP</h2>
      <p className="text-[#E8EBF0]/70 mt-3">Investors, educational institutions, museums, technology and content partners — tell us how you'd like to engage. We respond personally to each submission.</p>

      <form onSubmit={submit} className="mt-10 vault-card rounded-2xl p-7 border border-[#2A3349] space-y-4" data-testid="xr-partner-form">
        <Field label="Name *" testid="pf-name">
          <input required maxLength={80} value={form.name} onChange={(e) => upd("name", e.target.value)} className={fieldClasses} />
        </Field>
        <Field label="Organisation" testid="pf-org">
          <input maxLength={120} value={form.organisation} onChange={(e) => upd("organisation", e.target.value)} className={fieldClasses} />
        </Field>
        <Field label="Email *" testid="pf-email">
          <input type="email" required value={form.email} onChange={(e) => upd("email", e.target.value)} className={fieldClasses} />
        </Field>
        <Field label="Interest" testid="pf-interest">
          <div className="flex flex-wrap gap-2">
            {INTEREST_OPTIONS.map((o) => (
              <button key={o.v} type="button" onClick={() => upd("interest", o.v)} data-testid={`pf-interest-${o.v}`}
                className={`text-xs tracking-wide rounded-full px-3 py-1.5 border transition ${
                  form.interest === o.v ? "mythos-gold-bg text-[#0E0F12] border-[#C9A66B]" : "border-[#2A3349] text-[#F4EFE6]/70 hover:border-[#C9A66B]"
                }`}>
                {o.l}
              </button>
            ))}
          </div>
        </Field>
        <Field label="Message" testid="pf-message">
          <textarea rows={4} maxLength={2000} value={form.message} onChange={(e) => upd("message", e.target.value)} className={fieldClasses + " resize-none"} />
        </Field>
        {err && <div className="text-sm text-[#ff9b9b]" data-testid="xr-partner-error">{err}</div>}
        <button
          type="submit"
          disabled={status === "loading"}
          className="mt-2 mythos-gold-bg text-[#0E0F12] rounded-full px-7 py-3 font-bold inline-flex items-center gap-2 hover:opacity-90 disabled:opacity-50 transition"
          data-testid="xr-partner-submit"
        >
          {status === "loading" ? <Loader2 className="animate-spin" size={16} /> : <ArrowRight size={16} />}
          Submit Interest
        </button>
      </form>
    </section>
  );
}

const fieldClasses = "w-full bg-[#0F1623] border border-[#2A3349] focus:border-[#C9A66B] outline-none rounded-xl px-4 py-2.5 text-[#F4EFE6] placeholder-[#7d8294] text-sm";

function Field({ label, children, testid }) {
  return (
    <label className="block" data-testid={testid}>
      <div className="text-[10px] tracking-[0.22em] uppercase mythos-gold font-bold mb-2">{label}</div>
      {children}
    </label>
  );
}
